import React from "react";
import { connect } from "react-redux";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { getDayOrderDetails } from "../../internals/managers/ReportsManager";
import useThemeVersion from "../../internals/hooks/useThemeVersion";

ChartJS.register(ArcElement, Tooltip, Legend);

const BRAND_COLORS = [
  "#4E45E4", "#6F63FF", "#0E9F6E", "#FB923C",
  "#0C8FD4", "#F472B6", "#FBBF24", "#60A5FA",
  "#818CF8", "#4ADE80",
];

// Resolve design tokens at render time so the chart follows the active theme.
const cssVar = (name: string, fallback: string) => {
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v || fallback;
};

const DoughnutChart = (props: any) => {
  const { dayOrders } = props;
  useThemeVersion();
  const { labels, datasets, ordersArray } = getDayOrderDetails(dayOrders);

  const textColor = cssVar("--text", "#131318");
  const mutedColor = cssVar("--text3", "#8C8C9B");

  // No sales yet: the manager returns a single placeholder datum with no
  // label — give it a neutral track color instead of Chart.js's default.
  const colorList = labels.length
    ? labels.map((_: any, i: number) => BRAND_COLORS[i % BRAND_COLORS.length])
    : [cssVar("--surface3", "#EDEDF2")];

  const centerTextPlugin = {
    id: "centerText",
    beforeDraw(chart: any) {
      const { width, height, ctx } = chart;
      const total = (chart.data.datasets[0]?.data as number[]).reduce(
        (a: number, b: number) => a + b, 0
      );
      ctx.save();
      const cx = width / 2;
      const cy = height / 2 - 12;
      ctx.font = "600 20px 'JetBrains Mono', monospace";
      ctx.fillStyle = textColor;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(ordersArray.length, cx, cy);
      ctx.font = "11px 'Instrument Sans', sans-serif";
      ctx.fillStyle = mutedColor;
      ctx.fillText("items", cx, cy + 22);
      ctx.restore();
    },
  };

  const options: any = {
    maintainAspectRatio: false,
    responsive: true,
    cutout: "72%",
    layout: { padding: 4 },
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          font: { size: 11, family: "Instrument Sans" },
          color: textColor,
          padding: 12,
          boxWidth: 10,
          boxHeight: 10,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        backgroundColor: "#272848",
        titleColor: "#ACADC4",
        bodyColor: "#ffffff",
        bodyFont: { size: 12, weight: "bold", family: "Instrument Sans" },
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (ctx: any) => ` Rs ${ctx.parsed.toLocaleString()}`,
        },
      },
    },
  };

  const data: any = {
    labels,
    datasets: [
      {
        data: datasets,
        backgroundColor: colorList,
        hoverBackgroundColor: colorList.map((c: string) => c + "DD"),
        borderWidth: 0,
        hoverOffset: 6,
      },
    ],
  };

  return (
    <Doughnut
      plugins={[centerTextPlugin]}
      options={options}
      data={data}
    />
  );
};

export default connect(
  (state: any) => ({
    dayOrders: state.dashboard.get("dayOrders"),
    dayReportSummary: state.dashboard.get("dayReportSummary"),
  }),
  () => ({})
)(DoughnutChart);
